import React, { useCallback, useEffect, useState } from 'react';
import {
  stockPortfolioService,
  StockAccountOption,
  SaveStockActivityBotRequest,
} from '../../services/stockPortfolioService';
import {
  fieldInputStyle,
  fieldLabelStyle,
} from '../crypto/ActivityBotSizingControls';

const DEFAULT_UNIVERSE = ['SPY', 'QQQ', 'AAPL', 'MSFT', 'NVDA', 'TSLA'];

export interface StockActivityBotCreateDrawerProps {
  open: boolean;
  onClose: () => void;
  defaultAccountId?: number | null;
  onSaved?: () => void;
}

const StockActivityBotCreateDrawer: React.FC<StockActivityBotCreateDrawerProps> = ({
  open,
  onClose,
  defaultAccountId,
  onSaved,
}) => {
  const [accounts, setAccounts] = useState<StockAccountOption[]>([]);
  const [loadingMeta, setLoadingMeta] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [referenceName, setReferenceName] = useState('');
  const [t212ConfigId, setT212ConfigId] = useState<number | ''>('');
  const [amount, setAmount] = useState('25');
  const [targetProfitPct, setTargetProfitPct] = useState('3');
  const [stopLossPct, setStopLossPct] = useState('2');
  const [continuousRun, setContinuousRun] = useState(true);
  const [macroLongBlockEnabled, setMacroLongBlockEnabled] = useState(true);

  const [isMobileViewport, setIsMobileViewport] = useState(
    () => (typeof window !== 'undefined' ? window.matchMedia('(max-width: 768px)').matches : false),
  );

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 768px)');
    const onChange = () => setIsMobileViewport(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  const resetForm = useCallback(() => {
    setReferenceName('');
    setAmount('25');
    setTargetProfitPct('3');
    setStopLossPct('2');
    setContinuousRun(true);
    setMacroLongBlockEnabled(true);
    setError(null);
  }, []);

  const loadMeta = useCallback(async () => {
    setLoadingMeta(true);
    setError(null);
    try {
      const accs = await stockPortfolioService.listStockAccounts();
      setAccounts(accs);
      if (defaultAccountId && accs.some(a => a.id === defaultAccountId)) {
        setT212ConfigId(defaultAccountId);
      } else if (accs[0]) {
        setT212ConfigId(accs[0].id);
      } else {
        setT212ConfigId('');
      }
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load Trading 212 accounts');
    } finally {
      setLoadingMeta(false);
    }
  }, [defaultAccountId]);

  useEffect(() => {
    if (!open) return;
    resetForm();
    void loadMeta();
  }, [open, resetForm, loadMeta]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !saving) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, saving, onClose]);

  const buildPayload = (): SaveStockActivityBotRequest | null => {
    const amt = Number(amount);
    const tp = Number(targetProfitPct);
    const sl = Number(stopLossPct);
    if (!t212ConfigId || !Number.isFinite(amt) || amt <= 0) return null;
    if (!Number.isFinite(tp) || tp <= 0 || !Number.isFinite(sl) || sl <= 0) return null;
    return {
      referenceName: referenceName.trim() || 'Stock activity bot',
      t212ConfigId: Number(t212ConfigId),
      amount: amt,
      maxOpenPositions: 1,
      targetProfitPct: tp,
      stopLossPct: sl,
      continuousRun,
      macroLongBlockEnabled,
      universeSymbols: DEFAULT_UNIVERSE,
    };
  };

  const handleSave = async () => {
    const payload = buildPayload();
    if (!payload) {
      setError('Check all fields: amount must be positive; TP and SL must be greater than 0%.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await stockPortfolioService.createStockActivityBot(payload);
      onSaved?.();
      onClose();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to save activity bot');
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    if (saving) return;
    onClose();
  };

  if (!open) return null;

  return (
    <>
      <div
        role="presentation"
        onClick={handleClose}
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(15, 23, 42, 0.45)',
          zIndex: 1300,
        }}
      />
      <aside
        role="dialog"
        aria-modal="true"
        aria-labelledby="stock-activity-bot-drawer-title"
        style={{
          position: 'fixed',
          top: 0,
          right: 0,
          left: isMobileViewport ? 0 : 'auto',
          height: isMobileViewport ? '100dvh' : '100%',
          maxHeight: isMobileViewport ? '100dvh' : undefined,
          width: isMobileViewport ? '100%' : 'min(520px, 100vw)',
          background: '#fff',
          boxShadow: '-8px 0 32px rgba(0,0,0,0.12)',
          zIndex: 1301,
          display: 'flex',
          flexDirection: 'column',
          fontFamily: 'Nunito, system-ui, sans-serif',
        }}
      >
        <div
          style={{
            padding: '20px 20px 16px',
            borderBottom: '1px solid #e2e8f0',
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 12,
            flexShrink: 0,
          }}
        >
          <div>
            <div id="stock-activity-bot-drawer-title" style={{ fontSize: 18, fontWeight: 800, color: '#1e293b' }}>
              New stock activity bot
            </div>
            <div style={{ fontSize: 13, color: '#64748b', marginTop: 4, lineHeight: 1.4 }}>
              Long-only Trading 212 automation — MARKET buy + STOP loss, polled take-profit exits
            </div>
          </div>
          <button
            type="button"
            onClick={handleClose}
            disabled={saving}
            aria-label="Close"
            style={{
              border: 'none',
              background: '#f1f5f9',
              borderRadius: 8,
              width: 32,
              height: 32,
              cursor: saving ? 'not-allowed' : 'pointer',
              fontSize: 18,
              lineHeight: 1,
              color: '#475569',
              flexShrink: 0,
            }}
          >
            ×
          </button>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px 24px' }}>
          {error && (
            <div
              role="alert"
              style={{
                marginBottom: 14,
                padding: '10px 12px',
                background: '#fef2f2',
                border: '1px solid #fecaca',
                borderRadius: 8,
                color: '#b91c1c',
                fontSize: 12,
              }}
            >
              {error}
            </div>
          )}

          {loadingMeta ? (
            <div style={{ fontSize: 13, color: '#94a3b8' }}>Loading accounts…</div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={fieldLabelStyle}>Reference name (optional)</label>
                <input
                  type="text"
                  maxLength={80}
                  value={referenceName}
                  onChange={e => setReferenceName(e.target.value)}
                  placeholder="e.g. US mega-cap long bot"
                  style={fieldInputStyle}
                />
              </div>

              <div style={{ gridColumn: '1 / -1' }}>
                <label style={fieldLabelStyle}>Trading 212 account</label>
                <select
                  value={t212ConfigId}
                  onChange={e => setT212ConfigId(e.target.value ? Number(e.target.value) : '')}
                  style={fieldInputStyle}
                >
                  {accounts.length === 0 && <option value="">No accounts connected</option>}
                  {accounts.map(acc => (
                    <option key={acc.id} value={acc.id}>
                      {acc.name} ({acc.environment})
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label style={fieldLabelStyle}>Amount per trade ($)</label>
                <input
                  type="number"
                  min={1}
                  step={1}
                  value={amount}
                  onChange={e => setAmount(e.target.value)}
                  style={fieldInputStyle}
                />
              </div>

              <div>
                <label style={fieldLabelStyle}>Target profit (%)</label>
                <input
                  type="number"
                  min={0.1}
                  step={0.1}
                  value={targetProfitPct}
                  onChange={e => setTargetProfitPct(e.target.value)}
                  style={fieldInputStyle}
                />
              </div>

              <div>
                <label style={fieldLabelStyle}>Stop loss (% — STOP order)</label>
                <input
                  type="number"
                  min={0.1}
                  step={0.1}
                  value={stopLossPct}
                  onChange={e => setStopLossPct(e.target.value)}
                  style={fieldInputStyle}
                />
              </div>

              <div style={{ gridColumn: '1 / -1' }}>
                <label style={fieldLabelStyle}>Universe (MVP)</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {DEFAULT_UNIVERSE.map(sym => (
                    <span
                      key={sym}
                      style={{
                        padding: '4px 10px',
                        borderRadius: 999,
                        fontSize: 11,
                        fontWeight: 700,
                        background: '#f1f5f9',
                        color: '#475569',
                        border: '1px solid #e2e8f0',
                      }}
                    >
                      {sym}
                    </span>
                  ))}
                </div>
              </div>

              <div style={{ gridColumn: '1 / -1' }}>
                <label
                  style={{
                    display: 'inline-flex',
                    alignItems: 'flex-start',
                    gap: 8,
                    fontSize: 13,
                    color: '#475569',
                    cursor: 'pointer',
                    userSelect: 'none',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={macroLongBlockEnabled}
                    onChange={e => setMacroLongBlockEnabled(e.target.checked)}
                    style={{ width: 14, height: 14, marginTop: 2, accentColor: '#086afb' }}
                  />
                  <span>
                    <strong style={{ color: '#334155' }}>MI macro long-block</strong>
                    <span style={{ display: 'block', marginTop: 4, fontSize: 11, color: '#94a3b8', lineHeight: 1.45 }}>
                      Skip new buys when Market Intelligence macro is RISK_OFF, GEOPOLITICAL_ENERGY, OIL_SUPPLY, or MACRO_RATES.
                    </span>
                  </span>
                </label>
              </div>

              <div style={{ gridColumn: '1 / -1' }}>
                <label
                  style={{
                    display: 'inline-flex',
                    alignItems: 'flex-start',
                    gap: 8,
                    fontSize: 13,
                    color: '#475569',
                    cursor: 'pointer',
                    userSelect: 'none',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={continuousRun}
                    onChange={e => setContinuousRun(e.target.checked)}
                    style={{ width: 14, height: 14, marginTop: 2, accentColor: '#086afb' }}
                  />
                  <span>
                    <strong style={{ color: '#334155' }}>Continuous run</strong>
                    <span style={{ display: 'block', marginTop: 4, fontSize: 11, color: '#94a3b8', lineHeight: 1.45 }}>
                      Keep scanning for new entries after each leg closes (max 1 open position, 1 new entry per day).
                    </span>
                  </span>
                </label>
              </div>

              <div
                style={{
                  gridColumn: '1 / -1',
                  padding: '10px 12px',
                  borderRadius: 8,
                  background: '#fffbeb',
                  border: '1px solid #fcd34d',
                  fontSize: 11,
                  color: '#92400e',
                  lineHeight: 1.5,
                }}
              >
                Each entry uses ~2 orders (buy + stop). Daily cap is 20 orders per T212 account; max order value $500.
                Start on a DEMO account before going live.
              </div>
            </div>
          )}
        </div>

        <div
          style={{
            padding: '14px 20px',
            borderTop: '1px solid #e2e8f0',
            display: 'flex',
            gap: 8,
            flexWrap: 'wrap',
            flexShrink: 0,
            background: '#fff',
            paddingBottom: isMobileViewport ? 'max(14px, env(safe-area-inset-bottom))' : 14,
          }}
        >
          <button
            type="button"
            onClick={() => void handleSave()}
            disabled={saving || loadingMeta || accounts.length === 0}
            style={{
              padding: '8px 16px',
              borderRadius: 999,
              border: 'none',
              background: '#086afb',
              color: '#fff',
              fontWeight: 700,
              fontSize: 12,
              cursor: saving || loadingMeta ? 'wait' : 'pointer',
            }}
          >
            {saving ? 'Saving…' : 'Save bot'}
          </button>
          <button
            type="button"
            onClick={handleClose}
            disabled={saving}
            style={{
              padding: '8px 16px',
              borderRadius: 999,
              border: '1px solid #e2e8f0',
              background: '#fff',
              color: '#334155',
              fontWeight: 600,
              fontSize: 12,
              cursor: saving ? 'wait' : 'pointer',
            }}
          >
            Cancel
          </button>
        </div>
      </aside>
    </>
  );
};

export default StockActivityBotCreateDrawer;
